import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Survey } from 'posthog-js';
import type { ReactElement } from 'react';

import { useAddToast, useToasts } from '../../contexts/ToastContext';
import { window } from '../../globals';
import { BrandedLoadingScreen } from '../BrandedLoadingScreen';

import { OnboardingSurveyFullscreen } from './OnboardingSurvey/OnboardingSurveyFullscreen';

type Props = {
  children: ReactElement;
};

type GateState =
  | { kind: 'pending' }
  | { kind: 'show'; survey: Survey }
  | { kind: 'pass' };

const TIMEOUT_MS = 3000;

/**
 * Routes active PostHog surveys to the right presentation surface and gates
 * the app shell during the post-signup onboarding survey.
 *
 * The onboarding survey is a blocking takeover — when the current user has
 * `onboardingSurveyCompletedAt == null`, the provider blocks the app shell
 * (rendering a branded loading screen, suppressing all toast survey dispatch)
 * until PostHog responds, then either shows the takeover or unblocks. A 3s
 * timeout fails open so a slow/unreachable PostHog cannot lock users out.
 *
 * Non-onboarding surveys (e.g. NPS) render as bottom-right toasts via
 * `addToast({ type: 'survey', ... })`, dispatched once per id per mount, and
 * only after the gate has resolved to `pass`.
 *
 * The transition to `pass` is sticky: once the user has been let through (by
 * completion, mismatch, timeout, or absence of PostHog), no later flag
 * re-evaluation transitions them back to `show`. This prevents the takeover
 * from re-mounting after submission and unmounting the ThanksStep.
 */
export const SurveyProvider = ({ children }: Props) => {
  const posthog = usePostHog();
  const addToast = useAddToast();
  const { hideToast } = useToasts();
  const dispatchedSurveyIdsRef = useRef<Set<string>>(new Set());

  const onboardingSurveyId = window.posthogOnboardingSurveyId;
  const hasCompletedOnboarding = window.user?.onboardingSurveyCompletedAt != null;

  const initialKind: GateState['kind'] = !posthog || hasCompletedOnboarding ? 'pass' : 'pending';
  const [gate, setGate] = useState<GateState>({ kind: initialKind });

  // Enqueues a survey as a bottom-right toast, deduplicated per id by
  // `dispatchedSurveyIdsRef` (a Set scoped to this provider mount). The dedupe
  // matters because PostHog's `onSurveysLoaded` / `onFeatureFlags` callbacks
  // can both deliver the same survey list back-to-back during initialization;
  // without the Set, the user would see the same NPS toast twice.
  //
  // The `toastId` returned by `addToast` is captured in the `onSurveySubmit`
  // closure so the success-confirmation toast can hide the original survey
  // toast on submit. The identity of this callback is stable across renders
  // (only `addToast` / `hideToast` are in the deps, and both come from
  // `ToastContext` which memoizes them), so it is safe to put in the
  // dependency arrays of the effects below.
  const dispatchSurveyToast = useCallback(
    (survey: Survey) => {
      if (dispatchedSurveyIdsRef.current.has(survey.id)) {
        return;
      }
      dispatchedSurveyIdsRef.current.add(survey.id);
      const toastId = addToast({
        type: 'survey',
        survey,
        onSurveySubmit: () => {
          addToast({
            type: 'success',
            content: 'Thank you for your time answering these questions!',
          });
          hideToast(toastId);
        },
      });
    },
    [addToast, hideToast],
  );

  // Resolves the pending gate the first time PostHog reports its surveys.
  //
  // We subscribe to two PostHog events because either can fire first depending
  // on cache state and network timing:
  //   - `onSurveysLoaded` fires once the surveys list has been fetched.
  //   - `onFeatureFlags` fires when flags resolve; we then re-query active
  //     surveys, whose evaluation depends on those flags.
  // Whichever lands first calls `handleSurveys`, which transitions the gate to
  // `show` (onboarding survey matched) or `pass` (no match).
  //
  // `isCancelled` guards against late PostHog callbacks writing into an
  // unmounted tree. The 3s timeout fails open to `pass` so a slow or
  // unreachable PostHog cannot lock users out of the app. Re-entry is
  // impossible — once `gate.kind` leaves `pending`, the early return at the
  // top keeps this effect inert for the rest of the component's lifetime.
  useEffect(() => {
    if (gate.kind !== 'pending' || !posthog) {
      return undefined;
    }
    let isCancelled = false;

    const handleSurveys = (surveys: Survey[]) => {
      if (isCancelled) {
        return;
      }
      const onboardingMatch = onboardingSurveyId
        ? surveys.find((s) => s.id === onboardingSurveyId)
        : undefined;
      if (onboardingMatch) {
        setGate({ kind: 'show', survey: onboardingMatch });
        return;
      }
      setGate({ kind: 'pass' });
    };

    const unsubscribeSurveys = posthog.onSurveysLoaded(handleSurveys);
    const unsubscribeFlags = posthog.onFeatureFlags(() => {
      posthog.getActiveMatchingSurveys(handleSurveys);
    });
    const timeoutId = window.setTimeout(() => {
      if (!isCancelled) {
        setGate({ kind: 'pass' });
      }
    }, TIMEOUT_MS);

    return () => {
      isCancelled = true;
      unsubscribeSurveys();
      unsubscribeFlags();
      window.clearTimeout(timeoutId);
    };
  }, [gate.kind, posthog, onboardingSurveyId]);

  // Dispatches non-onboarding surveys (e.g. NPS) as bottom-right toasts once
  // the gate has resolved to `pass`. Runs strictly after the takeover path,
  // so the onboarding survey is never enqueued as both a takeover and a
  // toast — the `survey.id !== onboardingSurveyId` filter is the belt, and
  // the gate ordering is the suspenders.
  //
  // `dispatchSurveyToast` deduplicates per id via `dispatchedSurveyIdsRef`,
  // so this effect re-firing (e.g. on a `posthog` reference change after
  // init) cannot double-show the same survey within a single mount.
  useEffect(() => {
    if (gate.kind !== 'pass' || !posthog) {
      return;
    }
    posthog.getActiveMatchingSurveys((surveys) => {
      surveys.forEach((survey) => {
        if (survey.id !== onboardingSurveyId) {
          dispatchSurveyToast(survey);
        }
      });
    });
  }, [gate.kind, posthog, onboardingSurveyId, dispatchSurveyToast]);

  if (gate.kind === 'pending') {
    return <BrandedLoadingScreen />;
  }
  if (gate.kind === 'show') {
    return (
      <OnboardingSurveyFullscreen
        survey={gate.survey}
        onClose={() => setGate({ kind: 'pass' })}
      />
    );
  }
  return children;
};
